"use client";

import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { LMS_USER_DATA_CACHE_COOKIE } from "./constants";
import { useAuth } from "@/components/AuthProvider";
interface LmsUserStatus {
  isEnterpriseUser: boolean;
  isEmailConfirmationNeeded: boolean;
}

const DEFAULT_LMS_USER_STATUS: LmsUserStatus = {
  isEnterpriseUser: false,
  isEmailConfirmationNeeded: false,
};

const useLmsUserStatus = () => {
  const [lmsUserStatus, setLmsUserStatus] = useState<LmsUserStatus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoggedIn } = useAuth();

  const fetchUserStatus = useCallback(async () => {
    if (!isLoggedIn) {
      setLmsUserStatus(DEFAULT_LMS_USER_STATUS);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check cookie first
      const cachedData = Cookies.get(LMS_USER_DATA_CACHE_COOKIE);
      if (cachedData) {
        try {
          const parsed: LmsUserStatus = JSON.parse(cachedData);
          setLmsUserStatus(parsed);
          setLoading(false);
          return;
        } catch (parseError) {
          // If parsing fails, remove the invalid cookie and continue to API call
          Cookies.remove(LMS_USER_DATA_CACHE_COOKIE);
        }
      }

      // Call the API route
      const response = await fetch("/api/lms/user", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        setLmsUserStatus(DEFAULT_LMS_USER_STATUS);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data) {
        setLmsUserStatus(DEFAULT_LMS_USER_STATUS);
        setLoading(false);
        return;
      }

      const result: LmsUserStatus = {
        isEnterpriseUser: data.enterpriseDashboard !== null,
        isEmailConfirmationNeeded: data.emailConfirmation?.isNeeded ?? false,
      };

      // Cache the result
      Cookies.set(LMS_USER_DATA_CACHE_COOKIE, JSON.stringify(result), {
        expires: 1, // 1 day
      });

      setLmsUserStatus(result);
    } catch (fetchError) {
      console.error("Error in useLmsUserStatus:", fetchError);
      setError(
        fetchError instanceof Error ? fetchError.message : "Unknown error",
      );

      // Return fallback without caching on API error
      setLmsUserStatus(DEFAULT_LMS_USER_STATUS);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchUserStatus();
  }, [fetchUserStatus]);

  const refetch = useCallback(() => {
    // Remove cache and refetch
    Cookies.remove(LMS_USER_DATA_CACHE_COOKIE);
    setError(null);
    fetchUserStatus();
  }, [fetchUserStatus]);

  return { lmsUserStatus, loading, error, refetch };
};

export default useLmsUserStatus;
